<i18n>
{
  "en": {
    "clause": "Clause",
    "create": "Create",
    "edit": "Edit",
    "emptyFieldError": "Required field",
    "freeText": "Add free text",
    "gridName": "Name you will have in the grid",
    "inBottom": "Show at the footer of the section (Shown in the header by default)",
    "isHighlighted": "Bold text",
    "isVisible": "Section visible in the menu",
    "maxCharsError": "Permitted maximum characters: 512",
    "section": "Section",
    "subsection": "Subsection",
    "title": "Section name",
    "titleHint": "Section name in menu"
  },
  "es": {
    "clause": "Apartado",
    "create": "Crear",
    "edit": "Editar",
    "emptyFieldError": "Campo obligatorio",
    "freeText": "Añadir texto libre",
    "gridName":  "Nombre que tendra en la cuadrilla",
    "inBottom": "Mostrar al pie de la sección (Por defecto se muestra en la cabecera)",
    "isHighlighted": "Texto en negrita",
    "isVisible": "Sección visible en el menu",
    "maxCharsError": "Máximo de carácteres permitido: 512",
    "section": "Sección",
    "subsection": "Subsección",
    "title": "Nombre de la sección",
    "titleHint": "Nombre que tendrá la sección en el menú"
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if=visible class="px-5 py-4">
      <v-card-title class="pa-0">
        {{ modalTitle }}
      </v-card-title>
      <v-container class="pa-0">
        <v-form v-model="valid" ref="form">
          <DynameatLangugageFieldGenerator
            v-if="allowEditMainFields"
            v-model="title"
            :label="$t('title')"
            :hint="$t('titleHint')"
            :type="'textArea'"
            :rules="titleRules"
            required
          />
          <v-switch
            v-model="section.is_visible"
            :label="$t('isVisible')"
            :disabled="section.has_managed_products"
            v-if="allowEditMainFields"
          />
          <DynameatLangugageFieldGenerator
            v-if="isFreeTextDisallowed"
            v-model="description"
            :label="$t('freeText')"
            :type="'textArea'"
            :rules="freeTextRules"
          />
          <v-switch
            v-if="isFreeTextDisallowed"
            v-model="section.description_bottom"
            :label="$t('inBottom')"
          />
          <v-switch
            v-if="isFreeTextDisallowed"
            v-model="section.description_highlighted"
            :label="$t('isHighlighted')"
          />
          <dynameat-modal-buttons
            @cancel="onCancel"
            @confirm="onConfirm"
            :confirmDisabled="!valid"
            :confirmText="$t('confirm')"
          />
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import { maxChars, notEmptyString } from '@core/helpers/formValidators';
import { Prop, Watch } from 'vue-property-decorator';
import { Components, MapGetters } from '@core/helpers/decorators';
import Component from 'vue-class-component';
import Vue from 'vue';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import DynameatLangugageFieldGenerator from '@dynameat/components/DynameatLanguageFieldGenerator';
import { MenuGridItemConfiguration } from '@menuGridGenerator/models';
import { Section } from '../models';

@Component
@Components({ DynameatModalButtons, DynameatLangugageFieldGenerator })
@MapGetters(['templateList', 'currentSection', 'clauseId', 'pointOfSaleLanguages'])
export default class SectionCreateEditModal extends Vue {
  @Prop({
    String,
    default: 'create',
    validator(type) {
      return type === 'create' || type === 'edit';
    }
  }) type;

  @Prop({ Number, default: 0 }) newOrder;

  @Prop({
    Object,
    validator(section) {
      return section instanceof Section;
    }
  }) section;

  @Prop({ Boolean, default: false }) isSection;

  @Prop({ type: Boolean, default: false }) isClause;

  @Prop({ Boolean, default: false }) visible;

  @Prop({ type: Boolean, default: false }) isMenuManaged;

  valid = false;

  title = {}

  description = {}

  gridItemConfig = new MenuGridItemConfiguration()

  @Prop({ String, default: 'templateGuid' }) templateGuid;

  get modalTitle() {
    if (this.isClause) return `${this.$t(this.type)} ${this.$t('section')}`;
    if (this.isSection) return `${this.$t(this.type)} ${this.$t('subsection')}`;
    return `${this.$t(this.type)} ${this.$t('clause')}`;
  }

  get titleRules() {
    return {
      alt1: [notEmptyString(this.$t('emptyFieldError')), maxChars(this.$t('maxCharsError'), 64)],
      alt2: [maxChars(this.$t('emptyFieldError'), 64)],
      alt3: [maxChars(this.$t('emptyFieldError'), 64)]
    };
  }

  get freeTextRules() {
    return {
      alt1: [maxChars(this.$t('maxCharsError'), 512)],
      alt2: [maxChars(this.$t('maxCharsError'), 512)],
      alt3: [maxChars(this.$t('maxCharsError'), 512)]
    };
  }

  get currentTemplate() {
    return this.templateList.find((el) => el.guid === this.templateGuid);
  }

  get isFreeTextDisallowed() {
    if (this.isClause) return !this.currentTemplate?.isOperationDisallowed('SECTION', 'SECTION_DESCRIPTION');
    if (this.isSection) return !this.currentTemplate?.isOperationDisallowed('SUBSECTION', 'SECTION_DESCRIPTION');
    return !this.currentTemplate?.isOperationDisallowed('CLAUSE', 'SECTION_DESCRIPTION');
  }

  get isGridItemNameDisallowed() {
    return !this.currentTemplate?.isOperationDisallowed('CLAUSE', 'GRID_NAME');
  }

  get isSectionManaged() {
    return this.section.has_managed_products;
  }

  get allowEditMainFields() {
    return (!this.isMenuManaged) || (this.isMenuManaged && !this.isSectionManaged);
  }

  @Watch('section')
  onUpdatedSection(updatedSection) {
    if (updatedSection) {
      this.pointOfSaleLanguages.forEach((el) => {
        this.title[el.alternative] = updatedSection[`title_${el.alternative}`];
        this.description[el.alternative] = updatedSection[`description_${el.alternative}`];
      });
      if (this.isGridItemNameDisallowed) {
        this.gridItemConfig = MenuGridItemConfiguration.toInstance(updatedSection.menu_grid_item_configuration?.serialize());
      }
    }
  }

  onCancel() {
    this.$emit('cancel');
  }

  onConfirm() {
    if (this.$refs.form.validate()) {
      this.pointOfSaleLanguages.forEach((el) => {
        this.section[`title_${el.alternative}`] = this.title[el.alternative];
        this.section[`description_${el.alternative}`] = this.description[el.alternative];
      });
      this.section.menu = this.$route.params.menuGuid;
      this.section.parent_section = this.currentSection ? this.currentSection.guid : null;
      this.section.type = this.currentSection ? this.currentSection.type + 1 : this.clauseId;
      if (this.section.type === 1 && this.isGridItemNameDisallowed) {
        this.section.menu_grid_item_configuration = this.gridItemConfig;
      }
      if (this.type === 'create') this.section.order = this.newOrder;
      this.$emit('confirm', this.section);
    }
  }
}
</script>
<style scoped>
.required label::after {
  content: " *";
  color: red;
}
</style>
